import React from 'react';
import ewtStore from '../EWTStore';
import { observer } from "mobx-react";
import { StickyTable, Row, Cell } from 'react-sticky-table';
import { SyncOutlined } from '@ant-design/icons';
import { theme, scoreColor } from '../theme';

const CUP_FONT_HEADER = 20;
const CUP_FONT_DATA = 20;
const CUP_FONT_POS = 18;
const CUP_FONT_THROUGH = 19;
const POS_COL_WIDTH = 44;
const ROUND_COL_WIDTH = 52;
const TIME_COL_WIDTH = 72;
const TOT_COL_WIDTH = 46;
const PHOTO_COL_WIDTH = 64;

const Leaderboard = observer(class Leaderboard extends React.Component {
	constructor(props) {
		super(props);
		this.showPlayer = this.showPlayer.bind(this);
		this.state = { fullPP: null }
	}

	showPlayer(player) {
		player = ewtStore.players[player.id];
		player.showRound = ewtStore.rounds[ewtStore.currentRoundID];
		ewtStore.showPlayerCard = player;
	}

	render() {
		if (!ewtStore.initialized || !ewtStore.players || !ewtStore.sortedPlayers) return (
			<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: theme.brandBlue }}>
				<SyncOutlined spin style={{ fontSize: 48 }} />
			</div>
		);

		var sortedPlayers = ewtStore.sortedPlayers.filter(
			(player) => player.group || player.total !== 9999
		);
		const cellHeader = {
			backgroundColor: theme.navy,
			border: 'none',
			padding: '8px 4px',
			fontSize: CUP_FONT_HEADER,
			fontWeight: 600,
			color: '#FFFFFF',
			textAlign: 'center',
			verticalAlign: 'middle',
			letterSpacing: '0.02em',
		};
		const dataCell = {
			borderBottom: `1px solid ${theme.border}`,
			borderLeft: 'none',
			borderRight: 'none',
			borderTop: 'none',
			textAlign: 'center',
			verticalAlign: 'middle',
			fontSize: CUP_FONT_DATA,
			padding: '8px 4px',
			color: theme.navy,
		};
		const posCol = {
			...dataCell,
			width: POS_COL_WIDTH,
			minWidth: POS_COL_WIDTH,
			maxWidth: POS_COL_WIDTH,
			fontSize: CUP_FONT_POS,
			fontWeight: 600,
			textAlign: 'left',
			padding: '8px 2px 8px 6px',
		};
		const playerCol = {
			...dataCell,
			textAlign: 'left',
			padding: '4px 6px 4px 8px',
			cursor: 'pointer',
			minWidth: 0,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			fontWeight: 500,
		};
		const photoWidth = 54;
		const photoHeight = 38;

		var currentRounds = ewtStore.getCurrentRounds();
		const currentRound =
			ewtStore.currentRoundID &&
			ewtStore.rounds[ewtStore.currentRoundID];
		const showRoundScore =
			currentRound &&
			(currentRound.open ||
				currentRound.finalized ||
				ewtStore.roundHasScores(currentRound.id));
		const showThroughCol = currentRound && !currentRound.finalized;
		const showTotCol = currentRounds.length > 1;
		const showTeeTimeCol = showThroughCol && !showRoundScore;
		const throughOrTimeColWidth = TIME_COL_WIDTH;
		const availableWidth = (ewtStore.panelWidth || 400) - 16;
		let fixedColWidth =
			POS_COL_WIDTH +
			PHOTO_COL_WIDTH +
			(showRoundScore ? ROUND_COL_WIDTH : 0) +
			(showThroughCol ? throughOrTimeColWidth : 0) +
			(showTotCol ? TOT_COL_WIDTH : 0);
		const playerColWidth = Math.max(48, availableWidth - fixedColWidth);

		const playerHeaderCol = {
			...cellHeader,
			width: playerColWidth,
			minWidth: playerColWidth,
			maxWidth: playerColWidth,
			textAlign: 'left',
			paddingLeft: 8,
		};
		const playerDataCol = {
			...playerCol,
			width: playerColWidth,
			minWidth: playerColWidth,
			maxWidth: playerColWidth,
		};
		const roundCol = {
			...dataCell,
			width: ROUND_COL_WIDTH,
			minWidth: ROUND_COL_WIDTH,
			maxWidth: ROUND_COL_WIDTH,
			fontWeight: 600,
		};
		const totCol = {
			...dataCell,
			width: TOT_COL_WIDTH,
			minWidth: TOT_COL_WIDTH,
			maxWidth: TOT_COL_WIDTH,
			fontWeight: 600,
		};
		const throughCol = {
			...dataCell,
			width: throughOrTimeColWidth,
			minWidth: throughOrTimeColWidth,
			maxWidth: throughOrTimeColWidth,
			fontSize: CUP_FONT_THROUGH,
			color: theme.slate,
			whiteSpace: 'nowrap',
		};
		const photoCol = {
			borderBottom: `1px solid ${theme.border}`,
			padding: '4px 2px',
			width: PHOTO_COL_WIDTH,
			minWidth: PHOTO_COL_WIDTH,
			maxWidth: PHOTO_COL_WIDTH,
			overflow: 'hidden',
			verticalAlign: 'middle',
			textAlign: 'center',
			boxSizing: 'border-box',
		};

		return (
			<div
				className="flexColumn flexGrow cupPanel cupPanel--flush cupPanel--bleed cupPanel--leaderboard"
				style={{
					flex: 1,
					width: '100%',
					boxSizing: 'border-box',
					overflow: 'hidden',
					minHeight: 0,
				}}
			>
				{sortedPlayers && (
					<div className="flexGrow flexColumn cupTableGap" style={{ minWidth: 0 }}>
						<StickyTable
							border={0}
							style={{
								fontSize: CUP_FONT_DATA,
								width: '100%',
								height: '100%',
								tableLayout: 'fixed',
							}}
						>
							<Row>
								<Cell style={{ ...cellHeader, width: POS_COL_WIDTH, minWidth: POS_COL_WIDTH, maxWidth: POS_COL_WIDTH, fontSize: CUP_FONT_POS, textAlign: 'left', padding: '8px 2px 8px 6px' }}>Pos</Cell>
								<Cell style={playerHeaderCol}>Player</Cell>
								{showRoundScore && (
									<Cell style={{ ...cellHeader, width: ROUND_COL_WIDTH, minWidth: ROUND_COL_WIDTH, maxWidth: ROUND_COL_WIDTH }}>
										{currentRound.shortName}
									</Cell>
								)}
								{showThroughCol && (
									<Cell style={{ ...cellHeader, width: throughOrTimeColWidth, minWidth: throughOrTimeColWidth, maxWidth: throughOrTimeColWidth }}>
										{showTeeTimeCol ? 'Time' : '#'}
									</Cell>
								)}
								{showTotCol && (
									<Cell style={{ ...cellHeader, width: TOT_COL_WIDTH, minWidth: TOT_COL_WIDTH, maxWidth: TOT_COL_WIDTH }}>Tot</Cell>
								)}
								<Cell style={{ ...cellHeader, width: PHOTO_COL_WIDTH, minWidth: PHOTO_COL_WIDTH, maxWidth: PHOTO_COL_WIDTH, padding: 0 }}></Cell>
							</Row>
							{sortedPlayers.map((player, index) => {
								const pictureURL =
									ewtStore.players[player.id] &&
									ewtStore.players[player.id].pictureURL;
								const throughDisplay =
									player.through === 0
										? player.group
											? player.group.teeTime
											: ""
										: player.through === 18
										? "F"
										: player.through;
								const isLeader = player.position === 1 || player.position === '1';
								const rowBg = isLeader
									? theme.leaderRow
									: index % 2 === 0
									? theme.surface
									: theme.surfaceAlt;
								const rowStyle = { backgroundColor: rowBg };

								return (
									<Row key={index}>
										<Cell style={{ ...posCol, ...rowStyle }}>{player.position}</Cell>
										<Cell style={{ ...playerDataCol, ...rowStyle }} onClick={() => this.showPlayer(player)}>
											{ewtStore.players[player.id] && ewtStore.players[player.id].shortName}
										</Cell>
										{showRoundScore && (
											<Cell style={{ ...roundCol, ...rowStyle }}>
												<nobr style={{ color: scoreColor(player.currentRoundNet) }}>
													{player.currentRoundNetStr}
												</nobr>
											</Cell>
										)}
										{showThroughCol && (
										<Cell
											style={{
												...throughCol,
												...rowStyle,
												color: player.through === 0 ? theme.slate : theme.navy,
												fontWeight: player.through === 18 ? 600 : 400,
											}}
										>
											{throughDisplay}
										</Cell>
										)}
										{showTotCol && (
											<Cell style={{ ...totCol, ...rowStyle }}>
												<nobr style={{ color: scoreColor(player.total) }}>{player.totalStr}</nobr>
											</Cell>
										)}
										<Cell style={{ ...photoCol, ...rowStyle }}>
											{pictureURL && (
												<img
													src={pictureURL}
													alt={""}
													style={{
														cursor: "pointer",
														width: photoWidth,
														height: photoHeight,
														maxWidth: "100%",
														objectFit: "cover",
														display: "block",
														margin: '0 auto',
														borderRadius: 6,
														border: `1px solid ${theme.border}`,
														boxSizing: 'border-box',
													}}
													onClick={(e) => {
														e.stopPropagation();
														this.setState({
															fullPP: this.state.fullPP ? null : pictureURL,
														});
													}}
												/>
											)}
										</Cell>
									</Row>
								);
							})}
						</StickyTable>
					</div>
				)}
				{this.state.fullPP && (
					<div
						className="playerPhotoOverlay"
						onClick={() => this.setState({ fullPP: null })}
					>
						<img
							src={this.state.fullPP}
							alt=""
							onClick={() => this.setState({ fullPP: null })}
						/>
					</div>
				)}
			</div>
		);
	}
});


export default Leaderboard;
